import { Box, Paper, Title, Stack, Text, Group, Badge, Grid } from '@mantine/core';
import { DRAWER_PADDING } from 'utils/general';
import { useTranslation } from 'react-i18next';
import { GroupBadge } from 'components/GroupBadge';
import { LogsList } from 'models/patientMedicalEvent';
import { formatDate } from 'utils/dates';
import { useAppContext } from 'context/hooks';
import { FormColumn } from 'components/Form/FormColumn';
import { showBlankingPeriodLabel } from 'utils/logs';

interface LogDetailsDrawerProps {
  log: LogsList;
}
export const LogDetailsDrawer = ({ log }: LogDetailsDrawerProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAppContext();

  const groupName = currentUser?.visibleGroups.find((group) => log.data.groupId === group.id)?.name ?? '-';

  return (
    <Box px={DRAWER_PADDING}>
      <Stack>
        <Title order={4}>{t('log_details_label')}</Title>
        {log.data.type && (
          <Group>
            <Badge color={'var(--mantine-color-gray-7)'} size={'lg'} radius={'sm'} variant={'filled'}>
              {t(`add_log_log_type_${log.data.type}`)}
            </Badge>
          </Group>
        )}
        <Text size={'sm'} mb={'md'} fs={'italic'}>
          {formatDate({ timestamp: log.data.loggedTimestamp || 0, formatWithTime: true })}
        </Text>
        <Paper radius={'md'} p={'md'} withBorder>
          <Grid align={'center'}>
            {groupName && (
              <FormColumn label={t('group_label')}>
                <GroupBadge key={log.data?.groupId} label={groupName} groupId={log.data?.groupId} />
              </FormColumn>
            )}

            {log.data.treatment?.method && (
              <FormColumn label={t('name_label')}>
                <Text>{t(`add_log_form_${log.data.treatment.method}`)}</Text>
              </FormColumn>
            )}

            {log.data.treatment?.procedure?.type && (
              <FormColumn label={t('add_log_procedure_type')}>
                <Text>{t(`add_log_form_${log.data.treatment.procedure.type}`)}</Text>
              </FormColumn>
            )}

            {log.data.treatment?.iteration && (
              <FormColumn label={t('add_log_iteration')}>
                <Text>{t(`add_log_form_${log.data.treatment?.iteration}`)}</Text>
              </FormColumn>
            )}

            {log.data.treatment?.procedure?.technology && (
              <FormColumn label={t('add_log_technology')}>
                <Text>{t(`add_log_form_${log.data.treatment.procedure.technology}`)}</Text>
              </FormColumn>
            )}

            {log.data.treatment?.procedure?.vendor && (
              <FormColumn label={t('add_log_vendor')}>
                <Text>{t(`add_log_form_${log.data.treatment.procedure.vendor}`)}</Text>
              </FormColumn>
            )}

            {log.data.treatment?.procedure?.blankingPeriodInWeeks && (
              <FormColumn label={t('add_log_blanking_period')}>
                <Text>{showBlankingPeriodLabel(log.data.treatment.procedure.blankingPeriodInWeeks)}</Text>
              </FormColumn>
            )}

            {log.data.treatment?.medication?.type && (
              <FormColumn label={t('add_log_medication_type')}>
                <Text>{t(`add_log_form_${log.data.treatment.medication.type}`)}</Text>
              </FormColumn>
            )}

            {log.data.treatment?.medication?.protocol && (
              <FormColumn label={t('add_log_medication_protocol')}>
                <Text>{t(`add_log_form_${log.data.treatment.medication.protocol}`)}</Text>
              </FormColumn>
            )}

            {log.data.diagnosis?.method && (
              <FormColumn label={t('add_log_method')}>
                <Text>{t(`add_log_form_${log.data.diagnosis.method}`)}</Text>
              </FormColumn>
            )}

            {log.data.diagnosis?.result && (
              <FormColumn label={t('add_log_result')}>
                <Text>{t(`add_log_form_${log.data.diagnosis.result}`)}</Text>
              </FormColumn>
            )}

            {log.data.comment && (
              <FormColumn label={t('note_label')}>
                <Text>{log.data.comment}</Text>
              </FormColumn>
            )}
          </Grid>
        </Paper>
      </Stack>
    </Box>
  );
};
